import useDeleteOpenApiKey from '@/api/mutations/account/useDeleteOpenApiKey';
import useGetMyOpenApiKeys from '@/api/queries/me/useGetMyOpenApiKeys';
import { useModal } from '@/hooks/useModal';
import CreateApiKeyModal from '@/ui/components/companySettings/components/CreateApiKeyModal';
import {
  AnchorButton,
  Button,
  ManageableItemsList,
  Modal,
  useToastContext,
} from '@payaca/ambrosia';

import { FC } from 'react';

type Modals = {
  CREATE_API_KEY: undefined;
  DELETE_API_KEY: { apiKeyId: string };
};

export type TProps = {
  readOnly?: boolean;
};

const CompanyIntegrationsTable: FC<TProps> = (props) => {
  const { readOnly } = props;

  const { openApiKeys } = useGetMyOpenApiKeys();

  const [modalState, modalDispatch] = useModal<keyof Modals, Modals>({
    activeModal: null,
  });

  const openApiKeysItems = (openApiKeys || []).map((key) => ({
    id: key.id,
    name: key.name,
    clientId: key.clientId,
  }));

  const { mutateAsync: deleteOpenApiKeyMutation } = useDeleteOpenApiKey();
  const { pushError } = useToastContext();

  const deleteOpenApiKey = async (apiKeyId: string) => {
    const response = await deleteOpenApiKeyMutation({ id: apiKeyId });
    if (
      response.deleteOpenApiKey?.__typename ===
      'DeleteOpenApiKeyResponseSuccess'
    ) {
      modalDispatch({ type: 'CLOSE_MODAL' });
    } else {
      pushError('Sorry, there was an error deleting the API key');
    }
  };

  return (
    <>
      <ManageableItemsList>
        <ManageableItemsList.HeaderBar
          heading="API keys"
          buttons={
            readOnly ? undefined : (
              <Button
                onClick={() => modalDispatch({ type: 'CREATE_API_KEY' })}
                variant="white"
                size="sm"
              >
                Create API key
              </Button>
            )
          }
          subHeading={
            <p>
              Generate API keys to use with the{' '}
              <AnchorButton
                href="https://docs.api.payaca.com/"
                variant="link-inline"
                target="_blank"
                rel="noreferrer"
              >
                Payaca API
              </AnchorButton>
              .
            </p>
          }
        />
        <ManageableItemsList.Table
          items={openApiKeysItems}
          uniqueKey="id"
          itemActions={
            readOnly
              ? []
              : [
                  {
                    label: 'Delete',
                    onClick: (row) => {
                      modalDispatch({
                        type: 'DELETE_API_KEY',
                        payload: { apiKeyId: row.id },
                      });
                    },
                  },
                ]
          }
        >
          <ManageableItemsList.Table.Column field="name" header="Name" />
          <ManageableItemsList.Table.Column
            field="clientId"
            header="Client ID"
          />
        </ManageableItemsList.Table>
      </ManageableItemsList>

      <Modal
        isOpen={modalState.activeModal === 'DELETE_API_KEY'}
        onClose={() => modalDispatch({ type: 'CLOSE_MODAL' })}
        title="Delete API key"
      >
        <Modal.Body>
          <p>Are you sure you want to delete this API key?</p>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              onClick={async () => {
                if (!modalState.payload?.apiKeyId) return;
                await deleteOpenApiKey(modalState.payload.apiKeyId);
              }}
              colour="red"
            >
              Delete
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>

      <CreateApiKeyModal
        isOpen={modalState.activeModal === 'CREATE_API_KEY'}
        onClose={() => modalDispatch({ type: 'CLOSE_MODAL' })}
      />
    </>
  );
};

export default CompanyIntegrationsTable;
